import { useState, useEffect, useImperativeHandle, forwardRef } from 'react'
import ButtonSubmitCancel from '../../general/ButtonSubmitCancel'
import Button from '../../general/Button'
import TmdbExternalLink from '../../general/TmdbExternalLink'
import Translation from '../../Translation/cont/Translation'
import getTranslationStringById from '../../Translation/func/getTranslationStringById'
import { ORIGIN_TMDB, ORIGIN_DATA_PROVIDER, ORIGIN_PM } from '../../../constants/metaMovieOrigins'
import OverlayWrapper from '../../overlay/presentational/OverlayWrapper'
import Select from 'react-select'
import getImageUrl from '../../../functions/getImageUrl'
import ImageWithFallback from '../../general/ImageWithFallback'
import buildTmdbImageSlots, { shouldWriteAmbient } from '../../../functions/buildTmdbImageSlots'

const DATA_FIELDS = [
    { key: 'logo',            labelId: 'movie_edit_movie_logo',          sources: [ORIGIN_TMDB, ORIGIN_PM] },
    { key: 'title',           labelId: 'movie_edit_movie_title',          sources: [ORIGIN_TMDB, ORIGIN_DATA_PROVIDER] },
    { key: 'description',     labelId: 'movie_edit_movie_description',    sources: [ORIGIN_TMDB, ORIGIN_DATA_PROVIDER, ORIGIN_PM] },
    { key: 'genres',          labelId: 'movie_edit_movie_genres',         sources: [ORIGIN_TMDB, ORIGIN_DATA_PROVIDER, ORIGIN_PM] },
    { key: 'trailerURL',      labelId: 'movie_edit_movie_trailer',        sources: [ORIGIN_TMDB, ORIGIN_DATA_PROVIDER, ORIGIN_PM] },
    { key: 'actors',          labelId: 'movie_attributes_orig_actors',    sources: [ORIGIN_DATA_PROVIDER, ORIGIN_TMDB] },
    { key: 'original_title',  labelId: 'movie_attributes_orig_title',     sources: [ORIGIN_TMDB, ORIGIN_DATA_PROVIDER, ORIGIN_PM] },
]

const SOURCE_LABEL_IDS = {
    [ORIGIN_TMDB]: 'tmdb_label',
    [ORIGIN_DATA_PROVIDER]: 'source_ks',
    [ORIGIN_PM]: 'source_pm',
}

const LANGUAGE_OPTIONS = [
    { value: 'de', label: 'DE' },
    { value: 'en', label: 'EN' },
    { value: 'fr', label: 'FR' },
    { value: 'it', label: 'IT' },
    { value: 'es', label: 'ES' },
    { value: 'tr', label: 'TR' },
    { value: 'pl', label: 'PL' },
    { value: 'ru', label: 'RU' },
    { value: 'nl', label: 'NL' },
    { value: 'ja', label: 'JA' },
    { value: 'ko', label: 'KO' },
    { value: 'uk', label: 'UK' },
]

const SOURCE_CLASSES = {
    [ORIGIN_TMDB]: 'pill--tmdb',
    [ORIGIN_DATA_PROVIDER]: 'pill--ks',
    [ORIGIN_PM]: 'pill--pm',
}

const truncateDescription = (text) => {
    if (!text || text.length <= 220) return text
    let result = text.slice(0, 220)
    const nextSpace = text.indexOf(' ', 220)
    if (nextSpace !== -1) result = text.slice(0, nextSpace)
    return result + '…'
}

const formatActors = (actors) => {
    if (!actors || !Array.isArray(actors) || actors.length === 0) return ''
    return actors.map(a => a.name).filter(Boolean).join(', ')
}

const buildDefaultFieldOrigins = () => {
    const origins = {}
    DATA_FIELDS.forEach(f => { origins[f.key] = ORIGIN_TMDB })
    return origins
}

const TMDB_IMAGE_BASE = 'https://image.tmdb.org/t/p/'

const extractTmdbImageId = (url) => {
    if (!url) return null
    const match = url.match(/\/([^/]+)\.[^.]+$/)
    return match ? match[1] : null
}

const extractYouTubeKey = (url) => {
    if (!url) return null
    const watchMatch = url.match(/[?&]v=([^&]+)/)
    if (watchMatch) return watchMatch[1]
    const shortMatch = url.match(/youtu\.be\/([^?&]+)/)
    if (shortMatch) return shortMatch[1]
    const embedMatch = url.match(/\/embed\/([^?&]+)/)
    if (embedMatch) return embedMatch[1]
    return null
}

// the merged cliMovie value — a working provider ambient has an empty PM slot too,
// so this must read from `movie` (the merged value), never from `existingPmMovie`
const getEffectiveAmbientUrl = (movie) =>
    movie && movie.images && movie.images.ambient ? movie.images.ambient.url : ''

const TmdbLinkForm = forwardRef(({
    metaMoviePk, movies, movieMappings, pmMovies,
    searchResults, searchLoading, details, linkLoading, tmdbMovies,
    onSearch, onSelectResult, onClear,
    onMatch, onUnmatch, onNavigate, backPath,
    hidePills = false,
    initialQuery = '',
    translationStrings, currentLang,
}, ref) => {
    const movie = movies && metaMoviePk ? movies[metaMoviePk] : null
    const mapping = movieMappings && metaMoviePk ? movieMappings[metaMoviePk] : null
    const isAlreadyLinked = !!(mapping && mapping.tmdb)
    const linkedTmdbMovie = isAlreadyLinked && tmdbMovies ? tmdbMovies[mapping.tmdb] : null

    const pmMoviePk = mapping ? mapping.pm : null
    const existingPmMovie = pmMoviePk && pmMovies ? pmMovies[pmMoviePk] : null
    const hasProviderMovie = !!(mapping && mapping.source && mapping.source.length > 0)

    const [query, setQuery] = useState(initialQuery || (movie ? (movie.title || '') : ''))
    const [year, setYear] = useState('')
    const [language, setLanguage] = useState('de')
    const [logoLanguage, setLogoLanguage] = useState('de')
    const [posterLanguage, setPosterLanguage] = useState('de')
    const [backdropLanguage, setBackdropLanguage] = useState('de')
    const [selectedTmdbId, setSelectedTmdbId] = useState(null)
    const [fieldOrigins, setFieldOrigins] = useState(buildDefaultFieldOrigins)
    const [selectedPosterUrl, setSelectedPosterUrl] = useState(null)
    const [selectedLogoUrl, setSelectedLogoUrl] = useState(null)
    const [selectedBackdropUrls, setSelectedBackdropUrls] = useState(null)
    const [selectedTrailerKey, setSelectedTrailerKey] = useState(null)
    const [imageConflict, setImageConflict] = useState(null)
    const [backdropMergeMode, setBackdropMergeMode] = useState('add')
    const [showAllActors, setShowAllActors] = useState(false)
    const [hasSearched, setHasSearched] = useState(false)
    const [collapsedSections, setCollapsedSections] = useState({ logo: true, poster: true, backdrop: true, trailer: true })

    useEffect(() => {
        setQuery(movie ? (movie.title || '') : '')
        setYear('')
        setSelectedTmdbId(null)
        setFieldOrigins(buildDefaultFieldOrigins())
        setSelectedPosterUrl(null)
        setSelectedLogoUrl(null)
        setSelectedBackdropUrls(null)
        setSelectedTrailerKey(null)
        setCollapsedSections({ logo: true, poster: true, backdrop: true, trailer: true })
        setImageConflict(null)
        onClear()
    }, [metaMoviePk])

    const handleSearch = (e) => {
        e.preventDefault()
        setHasSearched(true)
        onSearch(query, year, language)
    }

    const handleSelectResult = (result) => {
        setSelectedTmdbId(result.id)
        setSelectedPosterUrl(null)
        setSelectedLogoUrl(null)
        setSelectedBackdropUrls(null)
        setSelectedTrailerKey(null)
        setLogoLanguage(language)
        setPosterLanguage(language)
        setBackdropLanguage(language)
        onSelectResult(result.id, language)
    }

    const handleOriginChange = (fieldKey, origin) => {
        setFieldOrigins(prev => ({ ...prev, [fieldKey]: origin }))
        if (fieldKey === 'logo' && origin === ORIGIN_TMDB) {
            setSelectedLogoUrl(null)
        }
        if (fieldKey === 'trailerURL' && origin === ORIGIN_TMDB) {
            setSelectedTrailerKey(null)
        }
    }

    const toggleSection = (key) => setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }))

    const handleSelectTrailer = (key) => {
        setSelectedTrailerKey(key)
        setFieldOrigins(prev => ({ ...prev, trailerURL: ORIGIN_PM }))
    }

    // TMDB has no short description, so it gets truncated from the overview.
    // For the other two origins the short description must never be derived from
    // the preview value: for ORIGIN_DATA_PROVIDER the preview falls back to the
    // merged cliMovie, which already carries the TMDB description once the movie
    // is linked — that leaked TMDB text into the PM short description.
    const buildDescriptionShort = (base) => {
        if (fieldOrigins.description === ORIGIN_TMDB) {
            return truncateDescription(selectedDetails && selectedDetails.description)
                || base.descriptionShort
                || null
        }
        if (fieldOrigins.description === ORIGIN_PM) {
            return base.descriptionShort || truncateDescription(base.description) || null
        }
        // ORIGIN_DATA_PROVIDER — backend serves the provider's own short description
        // via the description_short origin, keep the stored PM value untouched
        return base.descriptionShort || null
    }

    const buildSubmissionPayload = (mergeMode, { keepPoster = false } = {}) => {
        const detailsForSelected = details && selectedTmdbId ? details[String(selectedTmdbId)] : null
        const tmdbMoviePk = detailsForSelected ? detailsForSelected.pk : null
        if (!tmdbMoviePk) return null

        const movieTitle = movie ? (movie.title || '') : ''
        const effectivePosterUrl = !keepPoster && selectedPosterUrl ? selectedPosterUrl : null
        const effectiveLogoUrl = selectedLogoUrl || null

        const allTmdbUrls = ((selectedDetails && selectedDetails.backdrop_urls) || []).map(b => b.url)
        const imageSlots = buildTmdbImageSlots({
            movieTitle,
            selectedUrls: selectedBackdropUrls || allTmdbUrls,
            mergeMode,
            effectiveAmbientUrl: getEffectiveAmbientUrl(movie),
            existingPmBackgrounds: existingPmMovie && existingPmMovie.images && existingPmMovie.images.backgrounds
                ? existingPmMovie.images.backgrounds
                : [],
            providerBackgrounds: movie && movie.images && movie.images.backgrounds
                ? movie.images.backgrounds
                : [],
        })

        const base = existingPmMovie || {}
        const baseImages = base.images || { poster: null, ambient: null, backgrounds: [] }

        const newPoster = effectivePosterUrl
            ? { id: null, format: null, alt: `${movieTitle} - Poster`, url: effectivePosterUrl, position: null, positionX: null, positionY: null }
            : baseImages.poster

        const newLogo = fieldOrigins.logo === ORIGIN_PM && effectiveLogoUrl
            ? { id: null, format: null, alt: `${movieTitle} - Logo`, url: effectiveLogoUrl, position: null, positionX: null, positionY: null }
            : (base.logo || null)

        const trailerURL = fieldOrigins.trailerURL === ORIGIN_PM && selectedTrailerKey
            ? `https://www.youtube.com/watch?v=${selectedTrailerKey}`
            : (base.trailerURL || null)

        return {
            tmdbMoviePk,
            metaMovie: {
                pk: metaMoviePk,
                logo: fieldOrigins.logo,
                title: fieldOrigins.title,
                description_short: fieldOrigins.description,
                description: fieldOrigins.description,
                genres: fieldOrigins.genres,
                trailerURL: fieldOrigins.trailerURL,
                actors: fieldOrigins.actors,
                original_title: fieldOrigins.original_title,
            },
            pmMovie: {
                pk: pmMoviePk,
                title: base.title || null,
                titleDisplay: base.titleDisplay || null,
                subTitle: base.subTitle || null,
                description: base.description || null,
                descriptionShort: buildDescriptionShort(base),
                genres: base.genres || [],
                posterAttribute: base.posterAttribute || null,
                logo: newLogo,
                // a null ambient from buildTmdbImageSlots means "do not touch this slot"
                images: {
                    poster: newPoster,
                    ambient: imageSlots.ambient || baseImages.ambient,
                    backgrounds: imageSlots.backgrounds,
                },
                displayDates: base.displayDates || null,
                order: base.order || { fraction: [0, 1], floatingPoint: 0.0 },
                trailerURL,
                showCountdown: base.showCountdown || false,
            },
            tmdbMovieData: selectedDetails,
        }
    }

    const doSubmitWithBackdrops = (backdropMergeMode, keepPoster = false) => {
        if (!selectedTmdbId || linkLoading) return
        const payload = buildSubmissionPayload(backdropMergeMode, { keepPoster })
        if (!payload) return
        onMatch(metaMoviePk, payload.tmdbMoviePk, payload.metaMovie, payload.pmMovie, payload.tmdbMovieData)
    }

    const handleSubmit = () => {
        if (!selectedTmdbId || linkLoading) return
        const hasPosterConflict = !!selectedPosterUrl && !!(existingPmMovie
            && existingPmMovie.images
            && existingPmMovie.images.poster
            && existingPmMovie.images.poster.url)

        // only 'replace' mode overwrites existing backdrops; 'add' mode preserves them
        const hasBackdropConflict = backdropMergeMode === 'replace'
            && !!(existingPmMovie
                && existingPmMovie.images
                && existingPmMovie.images.backgrounds
                && existingPmMovie.images.backgrounds.length > 0)

        // only an ambient that actually shows something needs a confirmation —
        // replacing an empty or broken one is the repair this feature exists for
        const currentAmbientUrl = getEffectiveAmbientUrl(movie)
        const hasAmbientConflict = !!currentAmbientUrl
            && shouldWriteAmbient(backdropMergeMode, currentAmbientUrl)
            && (selectedBackdropUrls || backdrops.map(b => b.url)).length > 0

        if (hasPosterConflict || hasBackdropConflict || hasAmbientConflict) {
            setImageConflict({ poster: hasPosterConflict, backdrops: hasBackdropConflict, ambient: hasAmbientConflict })
            return
        }

        doSubmitWithBackdrops(backdropMergeMode)
    }

    const handleUnmatch = () => {
        onUnmatch(metaMoviePk)
    }

    const selectedDetails = details && selectedTmdbId ? details[selectedTmdbId] : null

    const selectedDetailsPk = selectedDetails ? selectedDetails.pk : null

    useEffect(() => {
        if (!selectedTmdbId || !selectedDetails) return
        const items = selectedDetails.backdrop_urls || []
        setSelectedBackdropUrls(items.length > 0 ? items.map(b => b.url) : null)
    }, [selectedTmdbId, selectedDetailsPk])

    const logos = (selectedDetails && selectedDetails.logo_urls) || []
    const posters = (selectedDetails && selectedDetails.poster_urls) || []
    const backdrops = (selectedDetails && selectedDetails.backdrop_urls) || []

    const availableLogoLanguages = ['all', ...Array.from(new Set(logos.map(l => l.iso_639_1).filter(Boolean)))]
    const availablePosterLanguages = ['all', ...Array.from(new Set(posters.map(p => p.iso_639_1).filter(Boolean)))]
    const availableBackdropLanguages = ['all', ...Array.from(new Set(backdrops.map(b => b.iso_639_1).filter(Boolean)))]

    const effectiveLogoLanguage = logoLanguage === 'all' || logos.some(l => l.iso_639_1 === logoLanguage) ? logoLanguage : 'all'
    const effectivePosterLanguage = posterLanguage === 'all' || posters.some(p => p.iso_639_1 === posterLanguage) ? posterLanguage : 'all'
    const effectiveBackdropLanguage = backdropLanguage === 'all' || backdrops.some(b => b.iso_639_1 === backdropLanguage) ? backdropLanguage : 'all'

    const filteredLogos = effectiveLogoLanguage === 'all' ? logos : logos.filter(l => l.iso_639_1 === effectiveLogoLanguage)
    const filteredPosters = effectivePosterLanguage === 'all' ? posters : posters.filter(p => p.iso_639_1 === effectivePosterLanguage)
    const filteredBackdrops = effectiveBackdropLanguage === 'all' ? backdrops : backdrops.filter(b => b.iso_639_1 === effectiveBackdropLanguage)

    const trailers = selectedDetails && selectedDetails.videos
        ? selectedDetails.videos.filter(v => v.video_type === 'Trailer' && v.site === 'YouTube')
        : []

    const getPreviewValue = (fieldKey) => {
        const origin = fieldOrigins[fieldKey]
        if (origin === ORIGIN_TMDB) {
            if (!selectedDetails) return ''
            switch (fieldKey) {
                case 'title': return selectedDetails.title || ''
                case 'description': return selectedDetails.description || ''
                case 'genres': return selectedDetails.genres
                    ? (Array.isArray(selectedDetails.genres) ? selectedDetails.genres.join(', ') : selectedDetails.genres)
                    : ''
                case 'trailerURL': {
                    if (selectedTrailerKey) return `https://www.youtube.com/watch?v=${selectedTrailerKey}`
                    const trailer = selectedDetails.videos
                        ? selectedDetails.videos.find(v => v.video_type === 'Trailer' && v.site === 'YouTube')
                        : null
                    return trailer ? `https://www.youtube.com/watch?v=${trailer.key}` : ''
                }
                case 'actors': return formatActors(selectedDetails.actors)
                case 'original_title': return selectedDetails.original_title || ''
                default: return ''
            }
        }
        if (origin === ORIGIN_DATA_PROVIDER || origin === ORIGIN_PM) {
            if (!movie) return ''
            switch (fieldKey) {
                case 'title': return movie.titleDisplay || movie.title || ''
                case 'description': {
                    if (origin === ORIGIN_PM && existingPmMovie && existingPmMovie.description) return existingPmMovie.description
                    return movie.description || movie.descriptionShort || ''
                }
                case 'genres': return movie.genres
                    ? (Array.isArray(movie.genres) ? movie.genres.join(', ') : movie.genres)
                    : ''
                case 'trailerURL': {
                    if (origin === ORIGIN_PM) {
                        if (selectedTrailerKey) return `https://www.youtube.com/watch?v=${selectedTrailerKey}`
                        if (existingPmMovie && existingPmMovie.trailerURL) return existingPmMovie.trailerURL
                    }
                    return movie.trailerURL || ''
                }
                case 'actors': return formatActors(movie.actors)
                case 'original_title': return movie.origTitle || ''
                default: return ''
            }
        }
        return ''
    }

    const flyLogoUrl = (u) => u ? getImageUrl(u, false, false, false, true, false, true) : { finalImageURl: u, imageUrl: u }
    const flyPosterUrl = (u) => u ? getImageUrl(u, false, true, false, true) : { finalImageURl: u, imageUrl: u }

    const pmLogoUrl = selectedLogoUrl || (existingPmMovie && existingPmMovie.logo && existingPmMovie.logo.url) || null

    const getLogoPreview = (fieldKey) => {
        if (fieldKey !== 'logo') return null
        const origin = fieldOrigins[fieldKey]
        if (origin === ORIGIN_TMDB) {
            const logoSrc = selectedLogoUrl || (selectedDetails && (selectedDetails.logo_url || null))
            if (logoSrc) {
                const logoUrls = flyLogoUrl(logoSrc)
                return <ImageWithFallback src={logoUrls.finalImageURl} srcFallback={logoUrls.imageUrl} alt="Logo" style={{ maxHeight: '40px', maxWidth: '100%', objectFit: 'contain' }} />
            }
            return <><Translation id="tmdb_label" /> <Translation id="movie_edit_movie_logo" /></>
        }
        if (origin === ORIGIN_PM) {
            if (pmLogoUrl) {
                const logoUrls = flyLogoUrl(pmLogoUrl)
                return <ImageWithFallback src={logoUrls.finalImageURl} srcFallback={logoUrls.imageUrl} alt="Logo" style={{ maxHeight: '40px', maxWidth: '100%', objectFit: 'contain' }} />
            }
            return <><Translation id="source_pm" /> <Translation id="movie_edit_movie_logo" /></>
        }
        return null
    }

    const selectedRuntime = selectedDetails && selectedDetails.runtime
        ? selectedDetails.runtime
        : null

    const selectedPosterPath = selectedPosterUrl
        || (existingPmMovie && existingPmMovie.images && existingPmMovie.images.poster && existingPmMovie.images.poster.url)
        || (selectedDetails && (selectedDetails.posterUrl || selectedDetails.poster_url))
        || null

    const previewTitle = getPreviewValue('title')
    const previewDescription = getPreviewValue('description')
    const previewGenres = fieldOrigins.genres === ORIGIN_TMDB
        ? (selectedDetails && selectedDetails.genres
            ? (Array.isArray(selectedDetails.genres) ? selectedDetails.genres : [])
            : [])
        : (movie && movie.genres
            ? (Array.isArray(movie.genres) ? movie.genres : [])
            : [])

    const trailerPreviewKey = extractYouTubeKey(getPreviewValue('trailerURL'))

    useImperativeHandle(ref, () => ({
        getSelectionData: () => {
            if (!selectedTmdbId) return null
            return buildSubmissionPayload(backdropMergeMode)
        }
    }))

    return (
        <>
        {imageConflict && (() => {
            const existingPosterUrl = existingPmMovie?.images?.poster?.url || null
            const incomingPosterUrl = selectedPosterUrl || selectedDetails?.poster_url || null

            const getBackgroundUrl = (bg) => (typeof bg === 'string' ? bg : bg?.url || null)

            const existingBackdropUrls = (existingPmMovie?.images?.backgrounds || [])
                .map(getBackgroundUrl).filter(Boolean)
            const incomingBackdropUrlsRaw = selectedBackdropUrls || selectedDetails?.backdrop_urls || []
            // buildTmdbImageSlots promotes the first selected url into images.ambient
            // and writes one fewer background whenever that happens — mirror that
            // here (via the same shouldWriteAmbient) so the overlay's grid and count
            // agree with what actually gets written.
            const promotesAmbient = incomingBackdropUrlsRaw.length > 0
                && shouldWriteAmbient(backdropMergeMode, getEffectiveAmbientUrl(movie))
            const incomingBackdropUrls = promotesAmbient
                ? incomingBackdropUrlsRaw.slice(1)
                : incomingBackdropUrlsRaw

            const MAX_PREVIEW = 5

            const renderBackdropGrid = (urls, modifier) => {
                const total = urls.length
                const showOverflow = total > 6
                const visible = showOverflow ? urls.slice(0, MAX_PREVIEW) : urls
                const overflowCount = total - MAX_PREVIEW
                return (
                    <div className="tmdb-conflict-backdrop-grid">
                        {visible.map((url, i) => {
                            const urls = flyPosterUrl(url)
                            return (
                                <ImageWithFallback
                                    key={i}
                                    className={`tmdb-conflict-backdrop-thumb tmdb-conflict-backdrop-thumb--${modifier}`}
                                    src={urls.finalImageURl}
                                    srcFallback={urls.imageUrl}
                                    alt=""
                                />
                            )
                        })}
                        {showOverflow && (
                            <div
                                className={`tmdb-conflict-backdrop-thumb tmdb-conflict-backdrop-thumb--${modifier} tmdb-conflict-backdrop-overflow tmdb-conflict-backdrop-overflow--${modifier}`}
                                data-label={`+${overflowCount}`}
                            />
                        )}
                    </div>
                )
            }

            const conflictCount = [imageConflict.poster, imageConflict.backdrops, imageConflict.ambient]
                .filter(Boolean).length

            const titleId = conflictCount > 1
                ? 'tmdb_conflict_both_title'
                : imageConflict.poster
                    ? 'tmdb_conflict_poster_title'
                    : imageConflict.backdrops
                        ? 'tmdb_conflict_backdrops_title'
                        : 'tmdb_conflict_both_title'

            const confirmId = conflictCount > 1
                ? 'tmdb_conflict_replace_images'
                : imageConflict.poster
                    ? 'tmdb_conflict_replace_poster'
                    : imageConflict.backdrops
                        ? 'tmdb_conflict_replace_backdrops'
                        : 'tmdb_conflict_replace_images'

            return (
                <OverlayWrapper>
                    <div className="tmdb-conflict-card">

                        <div className="tmdb-conflict-header">
                            <div className="tmdb-conflict-dot" />
                            <div className="tmdb-conflict-title text-light-description">
                                <Translation id={titleId} />
                            </div>
                        </div>

                        <div className="tmdb-conflict-body">

                            {imageConflict.poster && (
                                <div className="tmdb-conflict-section">
                                    {conflictCount > 1 && (
                                        <div className="tmdb-conflict-section-label text-small-bold">
                                            <Translation id="tmdb_conflict_poster_label" />
                                        </div>
                                    )}
                                    <div className="tmdb-conflict-comparison">
                                        <div className="tmdb-conflict-side">
                                            <div className="tmdb-conflict-side-label text-light-uppercase">
                                                <Translation id="tmdb_conflict_current" />
                                            </div>
                                            {existingPosterUrl && (() => {
                                                const urls = flyPosterUrl(existingPosterUrl)
                                                return (
                                                    <ImageWithFallback
                                                        className="tmdb-conflict-img tmdb-conflict-img--current"
                                                        src={urls.finalImageURl}
                                                        srcFallback={urls.imageUrl}
                                                        alt="current poster"
                                                    />
                                                )
                                            })()}
                                        </div>
                                        <div className="tmdb-conflict-arrow">
                                            <div className="tmdb-conflict-arrow-shaft" />
                                            <div className="tmdb-conflict-arrow-head" />
                                        </div>
                                        <div className="tmdb-conflict-side">
                                            <div className="tmdb-conflict-side-label tmdb-conflict-side-label--incoming text-light-uppercase">
                                                <Translation id="tmdb_conflict_replacing" />
                                            </div>
                                            {incomingPosterUrl
                                                ? (() => {
                                                    const urls = flyPosterUrl(incomingPosterUrl)
                                                    return (
                                                        <ImageWithFallback
                                                            className="tmdb-conflict-img tmdb-conflict-img--incoming"
                                                            src={urls.finalImageURl}
                                                            srcFallback={urls.imageUrl}
                                                            alt="new poster"
                                                        />
                                                    )
                                                })()
                                                : <div className="tmdb-conflict-img tmdb-conflict-img--incoming" />
                                            }
                                        </div>
                                    </div>
                                </div>
                            )}

                            {imageConflict.ambient && (
                                <div className="tmdb-conflict-section">
                                    {conflictCount > 1 && (
                                        <div className="tmdb-conflict-section-label text-small-bold">
                                            <Translation id="images_movie_ambient_image" />
                                        </div>
                                    )}
                                    <div className="tmdb-conflict-comparison">
                                        <div className="tmdb-conflict-side">
                                            <div className="tmdb-conflict-side-label text-light-uppercase">
                                                <Translation id="tmdb_conflict_current" />
                                            </div>
                                            {/* imageConflict.ambient can only be true while currentAmbientUrl was truthy at
                                                the time it was set, but that snapshot can go stale: a program refresh
                                                landing while the overlay is still open can null movie.images.ambient,
                                                so this must re-read and guard rather than reach into the object. */}
                                            {getEffectiveAmbientUrl(movie) && (() => {
                                                const urls = flyPosterUrl(getEffectiveAmbientUrl(movie))
                                                return (
                                                    <ImageWithFallback
                                                        className="tmdb-conflict-img tmdb-conflict-img--current"
                                                        src={urls.finalImageURl}
                                                        srcFallback={urls.imageUrl}
                                                        alt="current ambient"
                                                    />
                                                )
                                            })()}
                                        </div>
                                        <div className="tmdb-conflict-arrow">
                                            <div className="tmdb-conflict-arrow-shaft" />
                                            <div className="tmdb-conflict-arrow-head" />
                                        </div>
                                        <div className="tmdb-conflict-side">
                                            <div className="tmdb-conflict-side-label tmdb-conflict-side-label--incoming text-light-uppercase">
                                                <Translation id="tmdb_conflict_replacing" />
                                            </div>
                                            {(() => {
                                                const incomingAmbientUrl = (selectedBackdropUrls || backdrops.map(b => b.url))[0]
                                                const urls = flyPosterUrl(incomingAmbientUrl)
                                                return (
                                                    <ImageWithFallback
                                                        className="tmdb-conflict-img tmdb-conflict-img--incoming"
                                                        src={urls.finalImageURl}
                                                        srcFallback={urls.imageUrl}
                                                        alt="new ambient"
                                                    />
                                                )
                                            })()}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {imageConflict.backdrops && (
                                <div className="tmdb-conflict-section">
                                    {conflictCount > 1 && (
                                        <div className="tmdb-conflict-section-label text-small-bold">
                                            <Translation id="tmdb_conflict_backdrops_label" />
                                        </div>
                                    )}
                                    <div className="tmdb-conflict-backdrop-comparison">
                                        <div className="tmdb-conflict-backdrop-side">
                                            <div className="tmdb-conflict-side-label text-light-uppercase">
                                                <Translation id="tmdb_conflict_current" />
                                            </div>
                                            {renderBackdropGrid(existingBackdropUrls, 'current')}
                                            <span className="tmdb-conflict-backdrop-badge tmdb-conflict-backdrop-badge--current text-small-bold">
                                                {existingBackdropUrls.length} <Translation id="movie_edit_tab_movie_images"/>

                                            </span>
                                        </div>
                                        <div className="tmdb-conflict-arrow tmdb-conflict-arrow--backdrops">
                                            <div className="tmdb-conflict-arrow-shaft" />
                                            <div className="tmdb-conflict-arrow-head" />
                                        </div>
                                        <div className="tmdb-conflict-backdrop-side">
                                            <div className="tmdb-conflict-side-label tmdb-conflict-side-label--incoming text-light-uppercase">
                                                <Translation id="tmdb_conflict_replacing" />
                                            </div>
                                            {renderBackdropGrid(incomingBackdropUrls, 'incoming')}
                                            <span className="tmdb-conflict-backdrop-badge text-small-bold">
                                                {incomingBackdropUrls.length} <Translation id="movie_edit_tab_movie_images"/>

                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                        </div>

                        <div className="tmdb-conflict-buttons">
                            <div className="cancel-button-wrapper">
                                <button className="button-cancel" onClick={() => setImageConflict(null)}>
                                    <span><Translation id="cancel_button" /></span>
                                </button>
                            </div>
                            {imageConflict.poster && (
                                <div className="cancel-button-wrapper">
                                    <button className="button-cancel" onClick={() => {
                                        setImageConflict(null)
                                        doSubmitWithBackdrops(backdropMergeMode, true)
                                    }}>
                                        <span><Translation id="tmdb_conflict_keep_poster" /></span>
                                    </button>
                                </div>
                            )}
                            <div className="save-button-wrapper">
                                <button className="button-save" onClick={() => {
                                    setImageConflict(null)
                                    doSubmitWithBackdrops(backdropMergeMode, false)
                                }}>
                                    <span><Translation id={confirmId} /></span>
                                </button>
                            </div>
                        </div>

                    </div>
                </OverlayWrapper>
            )
        })()}
        <div className="tmdb-link-page">
            <div className={'tmdb-link-layout' + (!selectedTmdbId ? ' tmdb-link-layout--search' : '')}>

                {/* Left column */}
                <div className="tmdb-link-left">

                    {/* Currently linked TMDB movie */}
                    {linkedTmdbMovie && (
                        <div>
                            <div className="tmdb-current-link">
                                {linkedTmdbMovie.poster_url && (() => {
                                    const urls = flyPosterUrl(linkedTmdbMovie.poster_url)
                                    return (
                                        <ImageWithFallback
                                            className="tmdb-current-link-poster"
                                            src={urls.finalImageURl}
                                            srcFallback={urls.imageUrl}
                                            alt={linkedTmdbMovie.title}
                                        />
                                    )
                                })()}
                                <div className="tmdb-current-link-info">
                                    <div className="tmdb-current-link-label text-xxssmall-light"><Translation id="tmdb_label" /></div>
                                    <div className="tmdb-current-link-title">{linkedTmdbMovie.title}</div>
                                    {linkedTmdbMovie.original_title && linkedTmdbMovie.original_title !== linkedTmdbMovie.title && (
                                        <div className="tmdb-current-link-original text-xsmall-light">{linkedTmdbMovie.original_title}</div>
                                    )}
                                    {linkedTmdbMovie.release_date && (
                                        <div className="tmdb-current-link-year text-xsmall-light">{linkedTmdbMovie.release_date.slice(0, 4)}</div>
                                    )}
                                    <TmdbExternalLink tmdbId={linkedTmdbMovie.tmdb_id} className="tmdb-current-link-external text-small-light" />
                                </div>
                            </div>
                            {isAlreadyLinked && (
                                <Button
                                    transId="unmatch_button"
                                    hideText={false}
                                    className="button-small"
                                    onClick={handleUnmatch}
                                    disabled={!!linkLoading}
                                />
                            )}
                        </div>
                    )}

                    {/* Search section */}
                    {!selectedTmdbId && <div className="tmdb-link-section tmdb-link-section--search">
                        <div className="tmdb-link-section-head">
                            <h3 className="tmdb-link-section-title text-small-bold"><Translation id="search_movie_title" /></h3>
                        </div>
                        <div className="tmdb-link-section-body">
                        <form className="tmdb-search-form" onSubmit={handleSearch}>
                            <input
                                className="tmdb-search-input tmdb-search-input--title search-input-field text-small-regular"
                                type="text"
                                placeholder={getTranslationStringById('movie_edit_movie_title', translationStrings, currentLang, 'Filmtitel')}
                                value={query}
                                onChange={e => setQuery(e.target.value)}
                            />
                            <input
                                className="tmdb-search-input tmdb-search-input--year text-small-regular"
                                type="text"
                                placeholder={getTranslationStringById('movie_attributes_orig_year', translationStrings, currentLang, 'Jahr')}
                                value={year}
                                onChange={e => setYear(e.target.value)}
                                maxLength={4}
                            />
                            <Select
                                classNamePrefix="select"
                                className="select-container tmdb-search-select"
                                isSearchable={false}
                                isClearable={false}
                                menuShouldBlockScroll
                                options={LANGUAGE_OPTIONS}
                                value={LANGUAGE_OPTIONS.find(opt => opt.value === language)}
                                onChange={option => setLanguage(option.value)}
                            />
                            <button
                                className="tmdb-search-btn text-small-bold"
                                type="submit"
                                disabled={searchLoading || !query}
                            >
                                <Translation id="search_button" />
                            </button>
                        </form>

                        {searchLoading && (
                            <div className="tmdb-search-loading text-small-light"><Translation id="search_loading" /></div>
                        )}

                        {!searchLoading && hasSearched && searchResults && searchResults.length === 0 && (
                            <div className="tmdb-search-loading text-small-light"><Translation id="content_no_movies_available" /></div>
                        )}

                        {searchResults && searchResults.length > 0 && (
                            <div className="tmdb-results-grid">
                                {searchResults.map(result => {
                                    const isSelected = selectedTmdbId === result.id
                                    const posterUrl = result.poster_path
                                        ? TMDB_IMAGE_BASE + 'w185' + result.poster_path
                                        : null


                                    return (
                                        <div
                                            key={result.id}
                                            className={'tmdb-result-card' + (isSelected ? ' tmdb-result-card--selected' : '')}
                                            onClick={() => handleSelectResult(result)}
                                        >
                                            {posterUrl ? (
                                                <img
                                                    className="tmdb-result-poster"
                                                    src={posterUrl}
                                                    alt={result.title}
                                                />
                                            ) : (
                                                <div className="tmdb-result-poster tmdb-result-poster--empty" />
                                            )}
                                            {isSelected && (
                                                <div className="tmdb-result-checkmark">&#10003;</div>
                                            )}
                                            <div className="tmdb-result-info">
                                                <div className="tmdb-result-title text-regular">{result.title}</div>
                                                <div className="tmdb-result-meta">
                                                    {[
                                                        (result.release_date || result.releaseDate || result.year)
                                                            ? String(result.release_date || result.releaseDate || result.year).slice(0, 4)
                                                            : null,
                                                        result.original_language ? result.original_language.toUpperCase() : null,
                                                    ].filter(Boolean).join(' · ')}
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                        </div>
                    </div>}

                    {/* Datenquellen section */}
                    {selectedTmdbId && !hidePills && (
                        <div className="tmdb-link-section tmdb-link-section--origins">
                            <div className="tmdb-link-section-head">
                                <h3 className="tmdb-link-section-title text-small-bold"><Translation id="data_sources_per_field" /></h3>
                            </div>
                            <div className="tmdb-link-section-body">
                            <table className="tmdb-origins-table">
                                <tbody>
                                    {DATA_FIELDS.map(field => {
                                        const activeOrigin = fieldOrigins[field.key]
                                        const previewValue = field.key === 'logo' ? null : getPreviewValue(field.key)
                                        const truncated = field.key === 'description'
                                            ? truncateDescription(previewValue)
                                            : null
                                        return (
                                            <tr key={field.key} className="tmdb-origins-row">
                                                <td className="tmdb-origins-label text-small-light"><Translation id={field.labelId} /></td>
                                                <td className="tmdb-origins-pills">
                                                    {field.sources
                                                        .filter(src => src !== ORIGIN_DATA_PROVIDER || hasProviderMovie)
                                                        .map(src => (
                                                            <button
                                                                key={src}
                                                                type="button"
                                                                className={
                                                                    'tmdb-origin-pill text-small-light ' +
                                                                    SOURCE_CLASSES[src] +
                                                                    (activeOrigin === src ? ' active' : '')
                                                                }
                                                                onClick={() => handleOriginChange(field.key, src)}
                                                            >
                                                                <Translation id={SOURCE_LABEL_IDS[src]} />
                                                            </button>
                                                        ))}
                                                </td>
                                                <td className={'tmdb-origins-preview text-small-light' + (activeOrigin === ORIGIN_DATA_PROVIDER ? ' tmdb-origins-preview--dp' : activeOrigin === ORIGIN_PM ? ' tmdb-origins-preview--pm' : '')}>
                                                    {field.key === 'logo' ? (
                                                        <div className="tmdb-origins-preview-text">{getLogoPreview(field.key)}</div>
                                                    ) : field.key === 'description' ? (
                                                        <div>
                                                            <div className="tmdb-origins-preview-text">{previewValue}</div>
                                                            {truncated && truncated !== previewValue && (
                                                                <div className="tmdb-origins-preview-truncated text-small-light">
                                                                    <em><Translation id="movie_edit_movie_short_description" />:</em> {truncated}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div className="tmdb-origins-preview-text">{previewValue}</div>
                                                    )}
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                            </div>
                        </div>
                    )}

                    {/* Logo picker */}
                    {selectedTmdbId && logos.length > 0 && (
                        <div className={'tmdb-link-section tmdb-link-section--logos' + (collapsedSections.logo ? ' tmdb-link-section--collapsed' : '')}>
                            <div className="tmdb-link-section-head tmdb-link-section-head--collapsible" onClick={() => toggleSection('logo')}>
                                <h3 className="tmdb-link-section-title text-small-bold"><Translation id="movie_edit_movie_logo" /></h3>
                                {availableLogoLanguages.length > 2 && (
                                    <div className="tmdb-image-lang-tabs">
                                        {availableLogoLanguages.map(lang => (
                                            <button key={lang} type="button"
                                                className={'tmdb-image-lang-tab text-small-regular' + (effectiveLogoLanguage === lang ? ' tmdb-image-lang-tab--active' : '')}
                                                onClick={e => { e.stopPropagation(); setLogoLanguage(lang) }}
                                            >{lang === 'all' ? 'Alle' : lang.toUpperCase()}</button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="tmdb-link-section-body">
                                <div className="tmdb-logo-grid">
                                    {filteredLogos.map(item => {
                                        const effectiveSelected = selectedLogoUrl !== null ? selectedLogoUrl : (selectedDetails.logo_url || null)
                                        const isSelected = item.url === effectiveSelected
                                        const logoUrls = flyLogoUrl(item.url)
                                        return (
                                            <label
                                                key={item.url}
                                                className={'tmdb-logo-item' + (isSelected ? ' tmdb-logo-item--selected' : '')}
                                            >
                                                <input
                                                    type="radio"
                                                    name="tmdb-logo"
                                                    value={item.url}
                                                    checked={isSelected}
                                                    onChange={() => {
                                                        setSelectedLogoUrl(item.url)
                                                        const isDefault = item.url === (selectedDetails && selectedDetails.logo_url)
                                                        setFieldOrigins(prev => ({ ...prev, logo: isDefault ? ORIGIN_TMDB : ORIGIN_PM }))
                                                    }}
                                                    className="tmdb-logo-radio"
                                                />
                                                <ImageWithFallback className="tmdb-logo-img" src={logoUrls.finalImageURl} srcFallback={logoUrls.imageUrl} alt="Logo" />
                                            </label>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Poster picker */}
                    {selectedTmdbId && posters.length > 0 && (
                        <div className={'tmdb-link-section tmdb-link-section--posters' + (collapsedSections.poster ? ' tmdb-link-section--collapsed' : '')}>
                            <div className="tmdb-link-section-head tmdb-link-section-head--collapsible" onClick={() => toggleSection('poster')}>
                                <h3 className="tmdb-link-section-title text-small-bold"><Translation id="selection_style_poster" /></h3>
                                {availablePosterLanguages.length > 2 && (
                                    <div className="tmdb-image-lang-tabs">
                                        {availablePosterLanguages.map(lang => (
                                            <button key={lang} type="button"
                                                className={'tmdb-image-lang-tab text-small-regular' + (effectivePosterLanguage === lang ? ' tmdb-image-lang-tab--active' : '')}
                                                onClick={e => { e.stopPropagation(); setPosterLanguage(lang) }}
                                            >{lang === 'all' ? 'Alle' : lang.toUpperCase()}</button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="tmdb-link-section-body">
                                <div className="tmdb-poster-grid">
                                    {filteredPosters.map(item => {
                                        const isSelected = item.url === selectedPosterUrl
                                        const posterUrls = flyPosterUrl(item.url)
                                        return (
                                            <label
                                                key={item.url}
                                                className={'tmdb-poster-item' + (isSelected ? ' tmdb-poster-item--selected' : '')}
                                            >
                                                <input
                                                    type="radio"
                                                    name="tmdb-poster"
                                                    value={item.url}
                                                    checked={isSelected}
                                                    onChange={() => setSelectedPosterUrl(item.url)}
                                                    className="tmdb-poster-radio"
                                                />
                                                <ImageWithFallback className="tmdb-poster-img" src={posterUrls.finalImageURl} srcFallback={posterUrls.imageUrl} alt="Poster" />
                                            </label>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Backdrop picker */}
                    {selectedTmdbId && backdrops.length > 0 && (
                        <div className={'tmdb-link-section tmdb-link-section--backdrops' + (collapsedSections.backdrop ? ' tmdb-link-section--collapsed' : '')}>
                            <div className="tmdb-link-section-head tmdb-link-section-head--collapsible" onClick={() => toggleSection('backdrop')}>
                                <h3 className="tmdb-link-section-title text-small-bold"><Translation id="images_movie_wallpapers" /></h3>
                                {availableBackdropLanguages.length > 2 && (
                                    <div className="tmdb-image-lang-tabs">
                                        {availableBackdropLanguages.map(lang => (
                                            <button key={lang} type="button"
                                                className={'tmdb-image-lang-tab text-small-regular' + (effectiveBackdropLanguage === lang ? ' tmdb-image-lang-tab--active' : '')}
                                                onClick={e => { e.stopPropagation(); setBackdropLanguage(lang) }}
                                            >{lang === 'all' ? 'Alle' : lang.toUpperCase()}</button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="tmdb-link-section-body">
                                {backdrops.length > 0 && (
                                    <>
                                        <Translation id="tmdb_backdrops_hint"/>
                                        <div className="tmdb-backdrop-merge-toggle">
                                            <span className="toggle-label text-small-light"><Translation id="tmdb_backdrops_replace" /></span>
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    className="checkbox tgl tgl-flat"
                                                    checked={backdropMergeMode === 'add'}
                                                    onChange={() => setBackdropMergeMode(backdropMergeMode === 'add' ? 'replace' : 'add')}
                                                />
                                                <span className="tgl-btn text-regular"></span>
                                            </label>
                                            <span className="toggle-label text-small-light"><Translation id="add_button" /></span>
                                        </div>
                                    </>
                                )}
                                {(() => {
                                    const filteredUrls = filteredBackdrops.map(b => b.url)
                                    const selected = selectedBackdropUrls || filteredUrls
                                    const allSelected = filteredUrls.every(u => selected.includes(u))
                                    return allSelected
                                        ? <Button transId="tmdb_deselect_all" hideText={false} className="button button-small gap-30"
                                            onClick={() => setSelectedBackdropUrls(prev => (prev || filteredUrls).filter(u => !filteredUrls.includes(u)))} />
                                        : <Button transId="tmdb_select_all" hideText={false} className="button button-small gap-30"
                                            onClick={() => setSelectedBackdropUrls(prev => [...new Set([...(prev || []), ...filteredUrls])])} />
                                })()}
                                <div className="tmdb-backdrop-grid">
                                    {filteredBackdrops.map(item => {
                                        const selected = selectedBackdropUrls || backdrops.map(b => b.url)
                                        const isSelected = selected.includes(item.url)
                                        const backdropUrls = flyPosterUrl(item.url)
                                        return (
                                            <label
                                                key={item.url}
                                                className={'tmdb-backdrop-item' + (isSelected ? ' tmdb-backdrop-item--selected' : '')}
                                            >
                                                <input
                                                    type="checkbox"
                                                    value={item.url}
                                                    checked={isSelected}
                                                    onChange={() => setSelectedBackdropUrls(prev => {
                                                        const current = prev || backdrops.map(b => b.url)
                                                        return current.includes(item.url)
                                                            ? current.filter(u => u !== item.url)
                                                            : [...current, item.url]
                                                    })}
                                                    className="tmdb-backdrop-checkbox"
                                                />
                                                <ImageWithFallback className="tmdb-backdrop-img" src={backdropUrls.finalImageURl} srcFallback={backdropUrls.imageUrl} alt="Backdrop" />
                                            </label>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Trailer picker */}
                    {selectedTmdbId && trailers.length > 0 && (
                        <div className={'tmdb-link-section tmdb-link-section--trailers' + (collapsedSections.trailer ? ' tmdb-link-section--collapsed' : '')}>
                            <div className="tmdb-link-section-head tmdb-link-section-head--collapsible" onClick={() => toggleSection('trailer')}>
                                <h3 className="tmdb-link-section-title text-small-bold"><Translation id="movie_edit_movie_trailer" /></h3>
                            </div>
                            <div className="tmdb-link-section-body">
                                <div className="tmdb-trailer-grid">
                                    {trailers.map(trailer => {
                                        const thumbUrl = `https://img.youtube.com/vi/${trailer.key}/mqdefault.jpg`
                                        const isSelected = selectedTrailerKey === trailer.key
                                        return (
                                            <label
                                                key={trailer.key}
                                                className={'tmdb-trailer-item' + (isSelected ? ' tmdb-trailer-item--selected' : '')}
                                            >
                                                <input
                                                    type="radio"
                                                    name="tmdb-trailer"
                                                    value={trailer.key}
                                                    checked={isSelected}
                                                    onChange={() => handleSelectTrailer(trailer.key)}
                                                    className="tmdb-trailer-radio"
                                                />
                                                <img
                                                    className="tmdb-trailer-img"
                                                    src={thumbUrl}
                                                    alt={trailer.name}
                                                />
                                                <div className="tmdb-trailer-name text-small-light">{trailer.name}</div>
                                            </label>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Action buttons */}
                    <div className="tmdb-link-actions">
                        <ButtonSubmitCancel
                            transIdSubmit="match_button"
                            transIdCancel="cancel_button"
                            onSubmit={handleSubmit}
                            onCancel={() => onNavigate(backPath)}
                            disabledSubmit={!selectedTmdbId || !!linkLoading}
                        />
                    </div>

                </div>

                {/* Right column — TMDB preview */}
                {selectedDetails && (
                    <div className="tmdb-link-right">
                        <div className="tmdb-preview-card">
                            {selectedPosterPath && (() => {
                                const urls = flyPosterUrl(selectedPosterPath)
                                return (
                                    <ImageWithFallback
                                        className="tmdb-preview-poster"
                                        src={urls.finalImageURl}
                                        srcFallback={urls.imageUrl}
                                        alt={selectedDetails.title}
                                    />
                                )
                            })()}
                            <div className="tmdb-preview-info">
                                {((fieldOrigins.logo === ORIGIN_TMDB && (selectedDetails && selectedDetails.logo_url)) || (fieldOrigins.logo === ORIGIN_PM && pmLogoUrl))
                                    ? (() => {
                                        const urls = flyLogoUrl(fieldOrigins.logo === ORIGIN_PM ? pmLogoUrl : selectedDetails.logo_url)
                                        return <ImageWithFallback className="tmdb-preview-logo" src={urls.finalImageURl} srcFallback={urls.imageUrl} alt="Logo" />
                                    })()
                                    : <div className="tmdb-preview-title">{previewTitle}</div>
                                }
                                {(() => {
                                    const previewOriginalTitle = getPreviewValue('original_title')
                                    return previewOriginalTitle && previewOriginalTitle !== previewTitle
                                        ? <div className="tmdb-preview-original-title text-small-light">{previewOriginalTitle}</div>
                                        : null
                                })()}
                                <div className="tmdb-preview-chips">
                                    {previewGenres.map(genre => (
                                        <span key={genre} className="tmdb-preview-chip tmdb-preview-chip--genre text-small-light">
                                            {genre}
                                        </span>
                                    ))}
                                    {selectedRuntime && (
                                        <span className="tmdb-preview-chip tmdb-preview-chip--runtime text-small-light">
                                            {selectedRuntime} min
                                        </span>
                                    )}
                                </div>
                                {previewDescription && (
                                    <div className="tmdb-preview-overview text-small-light">
                                        {previewDescription}
                                    </div>
                                )}
                                {(() => {
                                    const ACTORS_VISIBLE = 15
                                    const origin = fieldOrigins['actors']
                                    const rawActors = origin === ORIGIN_TMDB
                                        ? (selectedDetails && selectedDetails.actors) || []
                                        : (movie && movie.actors) || []
                                    if (!rawActors.length) return null
                                    const visibleActors = showAllActors ? rawActors : rawActors.slice(0, ACTORS_VISIBLE)
                                    const hiddenCount = rawActors.length - ACTORS_VISIBLE
                                    return (
                                        <div className="actor-chips-block">
                                            <div className="actor-chips-wrap">
                                                {visibleActors.map((actor, i) => (
                                                    <span key={i} className="actor-chip">{actor.name}</span>
                                                ))}
                                            </div>
                                            {rawActors.length > ACTORS_VISIBLE && (
                                                <button className="actors-toggle-btn" type="button" onClick={() => setShowAllActors(s => !s)}>
                                                    {showAllActors ? '– weniger anzeigen' : `+ ${hiddenCount} weitere anzeigen`}
                                                </button>
                                            )}
                                        </div>
                                    )
                                })()}
                                {trailerPreviewKey && (
                                    <a
                                        className="tmdb-preview-trailer"
                                        href={`https://www.youtube.com/watch?v=${trailerPreviewKey}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <img
                                            className="tmdb-preview-trailer-thumb"
                                            src={`https://img.youtube.com/vi/${trailerPreviewKey}/mqdefault.jpg`}
                                            alt="Trailer"
                                        />
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
        </>
    )
})

export default TmdbLinkForm
